import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import headerImg from '@groovehq/internal-design-system/lib/assets/images/envelope-open.png'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_ID_FOLDERS_CHANNELS_CREATION_FEEDBACK } from 'ducks/drawers/ids'
import { DRAWER_TYPE_CHANNELS_CREATION_FEEDBACK } from 'ducks/drawers/types'
import { doGmailImport } from 'ducks/mailboxes/actions'
import { buildDrawerQueryParam } from 'ducks/drawers/util'

import { drawerConfig } from '../Channels.data'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const FORM_SCHEMA = yup.object().shape({
  import_history: yup.boolean(),
  import_labels: yup.boolean(),
  close_tickets: yup.boolean(),
})

const formEmptyState = {
  // defaultValue has to match yup form schema
  import_history: true,
  import_labels: true,
  close_tickets: false,
}

const MailImport = ({
  onClose,
  drawerChannelType,
  drawerResourceId: channelId,
  drawerChannelType: channelType,
  drawerId,
}) => {
  const [isSaving, setIsSaving] = useState(null)
  const [errors, setErrors] = useState(null)

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer({
    id: DRAWER_ID_FOLDERS_CHANNELS_CREATION_FEEDBACK,
    type: DRAWER_TYPE_CHANNELS_CREATION_FEEDBACK,
    closeIgnoresStack: true,
  })

  const dispatch = useDispatch()
  const useFormMethods = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: formEmptyState,
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useFormMethods
  const isImportingHistory = watch('import_history')

  const handleOnClose = useCallback(
    () => {
      reset(formEmptyState)
      onClose()
    },
    [onClose, reset]
  )

  const onSubmit = useCallback(
    async data => {
      try {
        setIsSaving(true)
        setErrors(null)
        await dispatch(doGmailImport(channelId, data))
        setIsSaving(false)
        openNextDrawer(channelId, {
          query: {
            ...buildDrawerQueryParam(
              nextDrawerId,
              'drawerChannelType',
              channelType
            ),
          },
        })
      } catch (error) {
        setIsSaving(false)
        setErrors(error)
      }
    },
    [dispatch, openNextDrawer, nextDrawerId, channelId, channelType]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const formId = `${drawerId}-form`

  const footer = (
    <ModalBtns
      saveBtnText={
        <>
          {!isSaving && <span>Done</span>}
          {isSaving && (
            <span>
              Saving<AnimatedEllipsis />
            </span>
          )}
        </>
      }
      saveBtnHtmlType="submit"
      saveBtnDisabled={!isValid || isSaving}
      saveBtnForm={formId}
      tertiaryBtnText="Back"
      onClickTertiaryBtn={handleBack}
      tertiaryBtnDisabled={isSaving}
    />
  )

  return (
    <Drawer
      title={DEFAULT_DRAWER_TITLE}
      footer={footer}
      onClose={handleOnClose}
    >
      <HeaderWithImage
        className="grui mt-12 mb-10"
        imgWidth="120px"
        img={headerImg}
        alt="Email"
        title="Mail import"
        intro="Would you like to import the last 50 conversations to your mailbox?"
      />
      <MessageCard type="negative" className="grui mb-12" visible={!!errors}>
        An error occured importing your conversations. Please try again.
      </MessageCard>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div>
            <Checkbox id="import_history" {...register('import_history')}>
              Import email history
            </Checkbox>
            <div className="grui mt-5 ml-12">
              <p
                css={[
                  paragraph.styles.preflight,
                  text.styles.textMediumDark,
                  text.styles.italic,
                ]}
              >
                This will import your last 50 emails as open conversations into
                Groove so you can pick up right from where you left off.
              </p>
              {isImportingHistory && (
                <div className="grui mt-10">
                  <Checkbox id="import_labels" {...register('import_labels')}>
                    {`Import ${drawerChannelType} labels`}
                  </Checkbox>
                  <div className="grui mt-5 ml-12">
                    <p
                      css={[
                        paragraph.styles.preflight,
                        text.styles.textMediumDark,
                        text.styles.italic,
                      ]}
                    >
                      {`Import your existing labels to organize your emails the same way you do in
            ${drawerChannelType}.`}
                    </p>
                  </div>
                </div>
              )}
              {isImportingHistory && (
                <div className="grui mt-10">
                  <Checkbox
                    id="conversationClosedOption"
                    className="grui mt-3"
                    {...register('close_tickets')}
                  >
                    Import as &ldquo;closed&rdquo;
                  </Checkbox>
                  <div className="grui mt-5 ml-12">
                    <p
                      css={[
                        paragraph.styles.preflight,
                        text.styles.textMediumDark,
                        text.styles.italic,
                      ]}
                    >
                      Close all newly-imported conversations in Groove. Leave
                      unchecked to let Groove determine the opened or closed
                      state automatically.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </Drawer>
  )
}

export default MailImport
